// Every order (PLAN-offers.md B1): what organizations bought or owe. A card payment through Mollie applies an order
// by itself (PLAN-payments.md M3: its attempts open under the table); "Mark paid" is a bank transfer arriving. A paid
// order is refunded from its refunds panel (M5).
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { useQueryParams } from '../../hooks/useQueryParams';
import { dateTime } from '../../services/format';
import { OrderAmounts } from '../../components/vat';
import { OrderPaymentBadge, OrderPayments, PaymentsToggle } from '../../components/payment';
import { OrderRefunds, RefundsToggle } from '../../components/refund';
import { Alert, Badge, Cell, Field, Filters, PageTitle, Pager, Row, Select, Table, statusTone } from '../../components/ui';
import { OrderActions } from '../orgs/SubscriptionTab';

const PAGE_SIZE = 50;

export function OrdersList() {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const navigate = useNavigate();
  const { get, page, set, reset, setPage } = useQueryParams();
  // Pending by default: what staff act on. "all" lifts the filter.
  const raw = get('status');
  const status = raw === 'all' ? '' : raw || 'pending';
  const { data, error, loading, reload } = useAsync(() => api.orders({ status: status || undefined, page, page_size: PAGE_SIZE }), [status, page]);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fail, setFail] = useState<string | null>(null);
  const [payments, setPayments] = useState<number | null>(null);
  const [refunds, setRefunds] = useState<number | null>(null);
  const refunding = data?.items.find((o) => o.id === refunds);

  const run = async (name: string, fn: () => Promise<unknown>, done?: string) => {
    setBusy(name);
    setFail(null);
    setNotice(null);
    try {
      await fn();
      if (done) setNotice(done);
      reload();
    } catch (err) {
      setFail(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageTitle title={t('orders.title')} subtitle={t('orders.subtitle')} />
      <Filters onReset={reset}>
        <Field label={t('orders.statusLabel')}>
          <Select value={raw === 'all' ? 'all' : status} onChange={(e) => set({ status: e.target.value === 'pending' ? '' : e.target.value })} data-testid="filter-status">
            <option value="all">{t('common.any')}</option>
            {(['pending', 'paid', 'canceled', 'failed'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`orders.status.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
      </Filters>
      {notice && <div className="mb-3"><Alert tone="good">{notice}</Alert></div>}
      {(fail || error) && <div className="mb-3"><Alert>{fail || error}</Alert></div>}
      <Table head={['#', t('orders.organization'), t('orders.kindLabel'), t('orders.statusLabel'), t('orders.what'), t('orders.amount'), t('orders.vat'), t('orders.total'), t('orders.payment'), t('orders.createdAt'), t('orders.paidAt'), '']} empty={loading ? t('common.loading') : t('orders.none')}>
        {(data?.items ?? []).map((o) => (
          <Row key={o.id}>
            <Cell mono>{o.id}</Cell>
            <Cell>
              <Link to={`/organizations/${o.organization_id}?tab=subscription`} className="text-accent underline">
                {o.organization_name}
              </Link>
            </Cell>
            <Cell>{t(`orders.kind.${o.kind}`)}</Cell>
            <Cell>
              <Badge tone={statusTone(o.status)}>{t(`orders.status.${o.status}`)}</Badge>
            </Cell>
            <Cell className="max-w-[280px] truncate" title={o.lines.map((x) => x.description).join('; ')}>
              {o.lines.map((x) => x.description).join('; ') || '—'}
            </Cell>
            <OrderAmounts order={o} />
            <Cell><OrderPaymentBadge order={o} /></Cell>
            <Cell mono>{dateTime(o.created_at, l)}</Cell>
            <Cell mono>{dateTime(o.paid_at, l)}</Cell>
            <Cell>
              <div className="flex flex-wrap gap-1.5">
                <OrderActions order={o} busy={busy} run={run} onInvoiced={(id) => navigate(`/invoices/${id}`)} />
                <PaymentsToggle order={o} open={payments === o.id} onToggle={setPayments} />
                <RefundsToggle order={o} open={refunds === o.id} onToggle={setRefunds} />
              </div>
            </Cell>
          </Row>
        ))}
      </Table>
      {data && <Pager page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />}
      {payments !== null && <OrderPayments orderId={payments} onClose={() => setPayments(null)} />}
      {refunding && <OrderRefunds order={refunding} onClose={() => setRefunds(null)} onRefunded={reload} />}
    </div>
  );
}
